import { createClient, SupabaseClient } from "@supabase/supabase-js";

export const isSupabaseConfigured = () => {
  return !!(
    import.meta.env.VITE_SUPABASE_URL &&
    (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY)
  );
};

let supabase: SupabaseClient;

try {
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;

  // In production, env vars MUST be set — fail fast rather than silently
  // falling back to localhost which causes a 30-second timeout.
  const rawUrl = import.meta.env.VITE_SUPABASE_URL;
  const rawKey =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (isProd && (!rawUrl || !rawKey)) {
    throw new Error(
      "Supabase is not configured. VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY) must be set in your environment variables."
    );
  }

  // Default to local Supabase emulator in dev if env vars are missing.
  // Trim whitespace/newlines in case the env var was copy-pasted with trailing characters.
  const url = (rawUrl || "http://127.0.0.1:15432").trim();
  // Prefer the new publishable key format (sb_publishable_...) when available.
  // Fall back to legacy anon key (JWT) for local CLI/emulator and older projects.
  const key = (
    rawKey ||
    // Supabase emulator anon key (public, for local dev only)
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0"
  ).trim();

  // Hard guard: local dev must not accidentally talk to hosted Supabase.
  // This prevents surprises when env precedence changes (e.g. `.env` / Vercel env).
  if (isDev) {
    const allowHosted = (() => {
      const raw = import.meta.env.VITE_ALLOW_HOSTED_SUPABASE_IN_DEV;
      if (!raw) return false;
      return ["1", "true", "yes", "y", "on"].includes(String(raw).toLowerCase());
    })();

    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.toLowerCase();
      const isHostedSupabase = hostname.endsWith(".supabase.co");
      if (isHostedSupabase && !allowHosted) {
        throw new Error(
          `Refusing to use hosted Supabase in DEV: ${url}. ` +
            `Set VITE_ALLOW_HOSTED_SUPABASE_IN_DEV=true to override.`
        );
      }
    } catch (e) {
      // If URL parsing fails, do nothing here; createClient will throw a clearer error.
      if (e instanceof Error && e.message.startsWith("Refusing to use hosted Supabase")) {
        throw e;
      }
    }
  }
  // Base timeout for most requests. Auth operations (signup, login) get a
  // longer window because Supabase may send a confirmation email server-side
  // before responding, which can easily exceed 10 seconds.
  const REQUEST_TIMEOUT_MS = 10000;
  const AUTH_REQUEST_TIMEOUT_MS = 30000;

  const fetchWithTimeout: typeof fetch = async (input, init) => {
    const start = performance.now();
    const controller = new AbortController();

    // Auth endpoints (signup, token, verify, otp, etc.) need more time because
    // the server may be sending emails before returning.
    const urlStr =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const isAuthRequest =
      /\/auth\/v1\/(signup|token|verify|otp|magiclink|recover|user|logout|resend)/.test(urlStr);
    const timeoutMs = isAuthRequest ? AUTH_REQUEST_TIMEOUT_MS : REQUEST_TIMEOUT_MS;

    // Always enforce our timeout, even if the caller provided a signal.
    // If the caller aborts, propagate that to our controller as well.
    const upstreamSignal = init?.signal;
    const onUpstreamAbort = () => controller.abort(upstreamSignal?.reason ?? "Request cancelled");
    if (upstreamSignal) {
      if (upstreamSignal.aborted) {
        controller.abort(upstreamSignal.reason ?? "Request cancelled");
      } else {
        upstreamSignal.addEventListener("abort", onUpstreamAbort, {
          once: true,
        });
      }
    }

    const timeout = window.setTimeout(() => {
      controller.abort(`Request timed out after ${timeoutMs}ms`);
    }, timeoutMs);

    try {
      const response = await fetch(input, {
        ...(init ?? {}),
        signal: controller.signal,
      });
      return response;
    } finally {
      window.clearTimeout(timeout);
      if (upstreamSignal && !upstreamSignal.aborted) {
        upstreamSignal.removeEventListener("abort", onUpstreamAbort);
      }
      if (isDev) {
        const ms = Math.round(performance.now() - start);
        if (ms > 2000) {
          const method = (init as any)?.method ?? "GET";
          console.warn(`[supabase] Slow request (${ms}ms): ${method} ${String(input)}`); // eslint-disable-line no-console
        }
      }
    }
  };

  if (isDev) {
    // eslint-disable-next-line no-console
    console.info("[supabase] URL:", url);
    // eslint-disable-next-line no-console
    console.info(
      "[supabase] publishable key present:",
      Boolean(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY)
    );
    // eslint-disable-next-line no-console
    console.info(
      "[supabase] legacy anon key present:",
      Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY)
    );
  }

  // Pre-emptively clear expired tokens BEFORE creating the Supabase client.
  // When users return after token expiry, Supabase auto-refresh on init
  // fails with 400 "Invalid Refresh Token" errors. By checking and clearing
  // expired sessions synchronously first, we prevent the noisy error.
  try {
    const stored = localStorage.getItem("wihmedia-auth");
    if (stored) {
      const parsed = JSON.parse(stored);
      const expiresAt = parsed?.expires_at;
      // If token is expired or missing expiry, clear it now (synchronously)
      if (!expiresAt || expiresAt * 1000 < Date.now()) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.info("[supabase] Clearing expired session from localStorage");
        }
        localStorage.removeItem("wihmedia-auth");
        localStorage.removeItem("wihmedia-auth-user-cache");
      }
    }
  } catch {
    // If we can't parse/validate stored data, clear it to be safe
    try {
      localStorage.removeItem("wihmedia-auth");
      localStorage.removeItem("wihmedia-auth-user-cache");
    } catch {
      // ignore localStorage errors
    }
  }

  supabase = createClient(url, key, {
    global: {
      fetch: fetchWithTimeout,
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Avoid cross-project/localStorage collisions during local dev.
      storageKey: "wihmedia-auth",
    },
  });

  // User-friendly session expiration handling
  if (typeof window !== "undefined") {
    let warningShown = false;
    let lastActivity = Date.now();
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

    // Track user activity
    const updateActivity = () => {
      lastActivity = Date.now();
      warningShown = false;
    };
    ["mousedown", "keydown", "scroll", "touchstart"].forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check session expiration every 60 seconds
    setInterval(() => {
      try {
        const stored = localStorage.getItem("wihmedia-auth");
        if (!stored) {
          warningShown = false;
          return;
        }

        const parsed = JSON.parse(stored);
        const expiresAt = parsed?.expires_at;
        if (!expiresAt) return;

        const now = Date.now();
        const expiresAtMs = expiresAt * 1000;
        const timeUntilExpiry = expiresAtMs - now;
        const isInactive = now - lastActivity > INACTIVITY_THRESHOLD_MS;

        // Warn 5 minutes before expiration (only if user is active)
        if (
          timeUntilExpiry > 0 &&
          timeUntilExpiry <= FIVE_MINUTES_MS &&
          !warningShown &&
          !isInactive
        ) {
          warningShown = true;
          const minutes = Math.ceil(timeUntilExpiry / 60000);
          window.dispatchEvent(
            new CustomEvent("session-expiring", {
              detail: { minutes },
            })
          );
        }

        // Session expired
        if (expiresAtMs < now) {
          if (isDev) {
            // eslint-disable-next-line no-console
            console.info("[supabase] Session expired, signing out");
          }
          // Store current location for post-login redirect
          const currentPath = window.location.pathname + window.location.search;
          if (!currentPath.includes("/login") && !currentPath.includes("/signup")) {
            sessionStorage.setItem("wihmedia-return-url", currentPath);
          }
          // Clear storage and sign out
          localStorage.removeItem("wihmedia-auth");
          localStorage.removeItem("wihmedia-auth-user-cache");
          window.dispatchEvent(new CustomEvent("session-expired"));
          void supabase.auth.signOut();
          warningShown = false;
        }
      } catch {
        // Ignore errors in periodic check
      }
    }, 60000); // Check every 60 seconds
  }
} catch (error) {
  console.error("Supabase initialization failed:", error);
  throw new Error("Failed to initialize Supabase. Please check your configuration.");
}

export { supabase };
